import React, { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { Stats, statsApi } from '../api/stats';
import { Machine, Customer, Tag, Integrator } from '../types';
import { machineApi, customerApi } from '../api/machines';
import { integratorApi } from '../api/integrators';
import { tagApi } from '../api/tags';
import { useAuth } from '../context/AuthContext';
import IntegratorAutocomplete from '../components/IntegratorAutocomplete';

const Card: React.FC<{ label: string; value: number | string; color?: string; sub?: string }> = ({ label, value, color, sub }) => (
  <div className="card" style={{
    padding: '1.25rem',
    borderTop: `4px solid ${color || 'var(--color-primary)'}`,
    minWidth: 0,
  }}>
    <div className="eyebrow" style={{ marginBottom: '0.5rem', color: 'var(--color-text-muted)' }}>{label}</div>
    <div style={{ fontSize: '2rem', fontWeight: 700, color: color || 'var(--color-text)', letterSpacing: '-0.02em' }}>
      {value}
    </div>
    {sub && <div className="item-meta" style={{ marginTop: '0.25rem' }}>{sub}</div>}
  </div>
);

function pct(part: number, whole: number): number {
  if (!whole) return 0;
  return Math.round((part / whole) * 100);
}

const StatsPage: React.FC = () => {
  const { user } = useAuth();
  const isSuperAdmin = !!user?.roles?.includes('ADMIN');
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [machines, setMachines] = useState<Machine[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [integrators, setIntegrators] = useState<Integrator[]>([]);
  const [integratorFilter, setIntegratorFilter] = useState<Integrator | null>(null);
  const [machineId, setMachineId] = useState<string>('');
  const [customerNip, setCustomerNip] = useState<string>('');
  const [tagId, setTagId] = useState<string>('');

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await statsApi.get({
        machineId: machineId || undefined,
        customerNip: customerNip || undefined,
        tagId: tagId || undefined,
        integratorId: integratorFilter?.id || undefined,
      });
      setStats(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Nie udało się pobrać statystyk');
    } finally {
      setLoading(false);
    }
  }, [machineId, customerNip, tagId, integratorFilter]);

  useEffect(() => {
    Promise.all([machineApi.getAll(), customerApi.getAll(), tagApi.list()])
      .then(([m, c, t]) => {
        setMachines(m.data);
        setCustomers(c.data);
        setTags(t.data);
      })
      .catch((e) => console.warn('Failed to load filter options', e));
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) return;
    integratorApi.list()
      .then(({ data }) => setIntegrators(data))
      .catch(() => { /* filtr po firmie po prostu nie podpowie firm */ });
  }, [isSuperAdmin]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (!user?.roles?.includes('ADMIN') && !user?.roles?.includes('EMPLOYEE_ADMIN')) {
    return <Navigate to="/machines" replace />;
  }

  const filteredMachines = machines.filter((m) => {
    if (customerNip && m.ownerNip !== customerNip) return false;
    if (integratorFilter && m.integratorId !== integratorFilter.id) return false;
    return true;
  });

  const helpfulRate = stats && stats.aiMessages > 0
    ? Math.round((stats.aiMessagesHelpful / stats.aiMessages) * 100)
    : null;

  const verifiedRate = stats && stats.aiMessages > 0
    ? Math.round((stats.verifiedAiMessages / stats.aiMessages) * 100)
    : null;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Statystyki zgłoszeń</h1>
      </div>

      <div className="filters-bar">
        {isSuperAdmin && (
          <div className="filter-group">
            <IntegratorAutocomplete
              integrators={integrators}
              value={integratorFilter}
              onChange={(i) => {
                setIntegratorFilter(i);
                setMachineId('');
              }}
              label="Firma (wpisz NIP lub nazwę)"
            />
          </div>
        )}
        <div className="filter-group">
          <label className="form-label">Klient (NIP)</label>
          <select
            className="form-select"
            value={customerNip}
            onChange={(e) => {
              setCustomerNip(e.target.value);
              setMachineId('');
            }}
          >
            <option value="">Wszyscy klienci</option>
            {customers.map((c) => (
              <option key={c.id} value={c.nip}>
                {c.companyName ? `${c.companyName} (${c.nip})` : c.nip}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label className="form-label">Maszyna</label>
          <select
            className="form-select"
            value={machineId}
            onChange={(e) => setMachineId(e.target.value)}
          >
            <option value="">Wszystkie maszyny</option>
            {filteredMachines.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}{customerNip ? '' : ` · NIP ${m.ownerNip}`}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label className="form-label">Tag</label>
          <select
            className="form-select"
            value={tagId}
            onChange={(e) => setTagId(e.target.value)}
          >
            <option value="">Wszystkie tagi</option>
            {tags.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="auth-error">{error}</div>}

      {loading ? (
        <div className="empty-state">Ładowanie statystyk…</div>
      ) : !stats ? (
        <div className="empty-state">Brak danych</div>
      ) : (
        <>
          <h2 style={{ fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-muted)', marginTop: '1.25rem', marginBottom: '0.75rem' }}>
            Zgłoszenia
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <Card label="Wszystkie" value={stats.totalTickets} color="var(--color-primary)" />
            <Card label="Aktywne" value={stats.openTickets} color="var(--color-success)" sub={`${pct(stats.openTickets, stats.totalTickets)}% z całości`} />
            <Card label="Czeka na klienta" value={stats.awaitingCustomerTickets} color="var(--color-warning)" sub={`${pct(stats.awaitingCustomerTickets, stats.totalTickets)}% z całości`} />
            <Card label="Zamknięte" value={stats.closedTickets} color="var(--color-text-muted)" sub={`${pct(stats.closedTickets, stats.totalTickets)}% z całości`} />
          </div>

          <h2 style={{ fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-muted)', marginBottom: '0.75rem' }}>
            Odpowiedzi AI
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <Card label="Łącznie" value={stats.aiMessages} color="var(--color-primary)" />
            <Card
              label="Pomocne (TAK)"
              value={stats.aiMessagesHelpful}
              color="var(--color-success)"
              sub={helpfulRate != null ? `${helpfulRate}% ocenionych satysfakcjonujących` : undefined}
            />
            <Card label="Niepomocne (NIE)" value={stats.aiMessagesUnhelpful} color="var(--color-danger)" />
            <Card label="Bez oceny" value={stats.aiMessagesNoFeedback} color="var(--color-text-muted)" />
            <Card
              label="Zweryfikowane przez integratora"
              value={stats.verifiedAiMessages}
              color="var(--color-accent)"
              sub={verifiedRate != null ? `${verifiedRate}% potwierdzonych` : undefined}
            />
            <Card label="Niezweryfikowane" value={stats.unverifiedAiMessages} color="var(--color-warning)" />
          </div>
        </>
      )}
    </div>
  );
};

export default StatsPage;
